// components/ImageGallery.js
import { useCallback, useEffect, useState } from 'react'
import PDFPreviewModal from './PDFPreviewModal'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import './ImageGallery.css'

function ImageGallery ({
  images = [],
  compact = false,
  maxPreview = 3,
  clickHandler = true,
  marginTop = true
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [currentImage, setCurrentImage] = useState(0)
  const [pdfModalOpen, setPdfModalOpen] = useState(false)
  const [currentPdf, setCurrentPdf] = useState(null)

  const isVideoMedia = image =>
    image?.mediaType === 'video' ||
    (image?.url || '').match(/\.(mp4|webm|mov)$/i) ||
    (image?.filename || '').match(/\.(mp4|webm|mov)$/i)

  const isPDFMedia = image =>
    image?.mediaType === 'pdf' ||
    (image?.filename || image?.name || '').match(/\.pdf$/i) ||
    (image?.url || '').match(/\.pdf$/i)

  if (!images || images.length === 0) {
    return null
  }

  const openLightbox = useCallback(index => {
    if (!clickHandler) return
    const image = images[index]
    
    // Check if it's a PDF - open PDF modal instead
    if (isPDFMedia(image)) {
      setCurrentPdf({
        filePath: extractStoragePathFromUrl(image.url),
        fileName: image.filename || image.name || 'document.pdf',
        pageNumber: 1
      })
      setPdfModalOpen(true)
      return
    }
    
    setCurrentImage(index)
    setLightboxOpen(true)
  }, [clickHandler, images])

  // Extract storage path from Firebase Storage URL
  const extractStoragePathFromUrl = (url) => {
    if (!url) return null
    try {
      // Firebase Storage URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media
      const match = url.match(/\/o\/([^?]+)/)
      if (match && match[1]) {
        return decodeURIComponent(match[1])
      }
      // Alternative format: https://storage.googleapis.com/{bucket}/{path}
      const match2 = url.match(/storage\.googleapis\.com\/[^/]+\/(.+?)(?:\?|$)/)
      if (match2 && match2[1]) {
        return decodeURIComponent(match2[1])
      }
    } catch (e) {
      console.error('Error extracting storage path from URL:', e)
    }
    return null
  }

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false)
  }, [])

  const nextImage = useCallback(() => {
    setCurrentImage(prev => (prev + 1) % images.length)
  }, [images.length])

  const prevImage = useCallback(() => {
    setCurrentImage(prev => (prev - 1 + images.length) % images.length)
  }, [images.length])

  // Enable arrow key navigation while the lightbox is open
  useEffect(() => {
    if (!lightboxOpen) return

    const handleKeyDown = event => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        nextImage()
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        prevImage()
      } else if (event.key === 'Escape') {
        event.preventDefault()
        closeLightbox()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, nextImage, prevImage, closeLightbox])

  // Preload neighbouring images so switching feels instant
  useEffect(() => {
    if (!lightboxOpen || images.length === 0) return

    const preloadIndexes = [
      (currentImage + 1) % images.length,
      (currentImage - 1 + images.length) % images.length
    ]

    preloadIndexes.forEach(index => {
      const candidate = images[index]
      if (!candidate || isVideoMedia(candidate) || isPDFMedia(candidate) || !candidate.url) return
      const preloader = new Image()
      preloader.src = candidate.url
    })
  }, [lightboxOpen, currentImage, images])

  // Compact mode: Show only first few images with "+X more" indicator
  if (compact) {
    const visibleImages = images.slice(0, maxPreview)
    const remainingCount = images.length - maxPreview

    return (
      <div className='image-gallery-compact'style={{ marginTop: marginTop ? '0.75rem' : '0' }}>
        {visibleImages.map((image, index) => {
          const isVideo = isVideoMedia(image)
          const isPDF = isPDFMedia(image)
          return (
          <div
            key={index}
            className='gallery-thumbnail-compact'
            onClick={() => openLightbox(index)}
          >
            {isPDF ? (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#f5f5f5',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <PictureAsPdf style={{ fontSize: '32px', color: '#d32f2f' }} />
                <span style={{ fontSize: '10px', color: '#666' }}>PDF</span>
              </div>
            ) : isVideo ? (
              <video
                src={image.url}
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
              />
            ) : (
              <img src={image.url} alt={image.filename || `Image ${index + 1}`} />
            )}
          </div>
          )
        })}
        {remainingCount > 0 && (
          <div
            className='gallery-more-indicator'
            onClick={() => openLightbox(maxPreview)}
          >
            +{remainingCount}
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && (
          <div className='lightbox-overlay' onClick={closeLightbox}>
            <div
              className='lightbox-content'
              onClick={e => e.stopPropagation()}
            >
              <button className='lightbox-close' onClick={closeLightbox}>
                ✕
              </button>

              {/* Render video or image based on mediaType (PDFs are handled separately) */}
              {(images[currentImage].mediaType === 'video' ||
                (images[currentImage].url || '').match(/\.(mp4|webm|mov)$/i) ||
                (images[currentImage].filename || '').match(/\.(mp4|webm|mov)$/i)) ? (
                <video
                  src={images[currentImage].url}
                  controls
                  style={{ maxWidth: '90vw', maxHeight: '80vh' }}
                />
              ) : (
                <img
                  src={images[currentImage].url}
                  alt={
                    images[currentImage].filename || `Image ${currentImage + 1}`
                  }
                />
              )}

              <div className='lightbox-info'>
                {images[currentImage].filename && (
                  <span className='lightbox-filename'>
                    {images[currentImage].filename}
                  </span>
                )}
                <span className='lightbox-counter'>
                  {currentImage + 1} / {images.length}
                </span>
              </div>

              {images.length > 1 && (
                <>
                  <button className='lightbox-prev' onClick={prevImage}>
                    ‹
                  </button>
                  <button className='lightbox-next' onClick={nextImage}>
                    ›
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  // Full mode: Show all images in grid
  return (
    <div className='image-gallery-full'>
      <div className='gallery-grid'>
        {images.map((image, index) => {
          const isVideo = isVideoMedia(image)
          const isPDF = isPDFMedia(image)
          return (
          <div
            key={index}
            className='gallery-thumbnail'
            onClick={() => openLightbox(index)}
          >
            {isPDF ? (
              <div style={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: '#f5f5f5',
                flexDirection: 'column',
                gap: '8px'
              }}>
                <PictureAsPdf style={{ fontSize: '48px', color: '#d32f2f' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>PDF</span>
              </div>
            ) : isVideo ? (
              <video
                src={image.url}
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
              />
            ) : (
              <img src={image.url} alt={image.filename || `Image ${index + 1}`} />
            )}
            {image.filename && (
              <div className='thumbnail-label'>{image.filename}</div>
            )}
          </div>
          )
        })}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className='lightbox-overlay' onClick={closeLightbox}>
          <div className='lightbox-content' onClick={e => e.stopPropagation()}>
            <button className='lightbox-close' onClick={closeLightbox}>
              ✕
            </button>

            {/* Render video or image based on mediaType (PDFs are handled separately) */}
            {(images[currentImage].mediaType === 'video' ||
              (images[currentImage].url || '').match(/\.(mp4|webm|mov)$/i) ||
              (images[currentImage].filename || '').match(/\.(mp4|webm|mov)$/i)) ? (
              <video
                src={images[currentImage].url}
                controls
                style={{ maxWidth: '90vw', maxHeight: '80vh' }}
              />
            ) : (
              <img
                src={images[currentImage].url}
                alt={images[currentImage].filename || `Image ${currentImage + 1}`}
              />
            )}

            <div className='lightbox-info'>
              {images[currentImage].filename && (
                <span className='lightbox-filename'>
                  {images[currentImage].filename}
                </span>
              )}
              <span className='lightbox-counter'>
                {currentImage + 1} / {images.length}
              </span>
            </div>

            {images.length > 1 && (
              <>
                <button className='lightbox-prev' onClick={prevImage}>
                  ‹
                </button>
                <button className='lightbox-next' onClick={nextImage}>
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfModalOpen && currentPdf && (
        <PDFPreviewModal
          source={currentPdf}
          onClose={() => {
            setPdfModalOpen(false)
            setCurrentPdf(null)
          }}
        />
      )}
    </div>
  )
}

export default ImageGallery
